import { useState } from 'react'
import { api } from '../api/client'
import type { Profile, RunningInstance } from '../types/profile'

interface Props {
  instances: RunningInstance[]
  profiles: Profile[]
  selected: RunningInstance | null
  onSelect: (inst: RunningInstance | null) => void
  onStopped: () => void
}

export function BrowserView({ instances, profiles, selected, onSelect, onStopped }: Props) {
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function stop(profileId: string) {
    setBusyId(profileId)
    setError(null)
    try {
      await api.stopProfile(profileId)
      onStopped()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  if (instances.length === 0) {
    return (
      <div className="empty-state">
        <p>当前没有运行的浏览器实例。</p>
        <p>去「Profiles」标签页启动一个。</p>
      </div>
    )
  }

  return (
    <div className="browser-view">
      {error && <div className="error-banner">{error}</div>}
      <div className="instance-grid">
        {instances.map((inst) => {
          const profile = profiles.find((p) => p.id === inst.profileId)
          return (
            <div
              key={inst.profileId}
              className={`instance-card ${selected?.profileId === inst.profileId ? 'selected' : ''}`}
              onClick={() => onSelect(inst)}
            >
              <div className="instance-header">
                <strong>{profile?.name ?? inst.profileId}</strong>
                <span className={`badge ${inst.headless ? 'gray' : 'green'}`}>
                  {inst.headless ? 'headless' : 'headed'}
                </span>
              </div>
              <div className="instance-meta">
                <span>CDP: {inst.cdpPort}</span>
                <span>PID: {inst.pid}</span>
              </div>
              <button
                className="danger small"
                onClick={(e) => {
                  e.stopPropagation()
                  stop(inst.profileId)
                }}
                disabled={busyId === inst.profileId}
              >
                停止
              </button>
            </div>
          )
        })}
      </div>

      {selected && (
        <div className="instance-detail">
          <h3>{profiles.find((p) => p.id === selected.profileId)?.name}</h3>
          <p>
            CDP 端点：<code>{selected.cdpUrl}</code>
          </p>
          <p className="hint">
            远程浏览器查看需要后端暴露 VNC/novnc 端口，或在 Cloudflare Tunnel 配置里转发调试端口。
            目前 Cloudflare Tunnel 默认不支持原生 CDP WebSocket（端口转发受限），
            建议通过 <a href="https://developers.cloudflare.com/cloudflare-one/connections/connect-networks/" target="_blank" rel="noreferrer">Cloudflare One Tunnel</a> 开启 WARP-to-Tunnel 模式做完整代理。
          </p>
          <div className="cdp-actions">
            <a href={selected.cdpUrl} target="_blank" rel="noreferrer">
              在新标签页打开 CDP 描述（JSON）
            </a>
          </div>
        </div>
      )}
    </div>
  )
}